/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useRef, useState } from "react";

import { Node } from "slate";
import { useTheme } from "styled-components";

import { BlockType } from "@vienna/common";

import { LinkBlock, TextBlock } from "src/classes/block/block-types";
import { Plate } from "src/classes/plate/Plate";
import { StringUtils } from "src/classes/utilities/StringUtils";
import { SlashMenuOption, SLASH_MENU_OPTIONS } from "src/components/SlashMenu/slashMenuOptions";
import { ViennaEditor } from "src/components/ViennaEditor";
import { getTextContent } from "src/components/ViennaEditor/lib/helpers";
import { initializeViennaEditor } from "src/components/ViennaEditor/lib/initialize";

import { StyledTextBlockContent } from "./styles";

interface Props {
  editable: boolean;
  block: TextBlock;
  plate: Plate;
}
export function TextBlockContent({ block, editable, plate }: Props) {
  const initialValue = getTextContent(block.snapshot);
  const theme = useTheme();
  const blockRef = useRef<HTMLDivElement>(null);
  const selection = plate.observe((state) => state.selection);
  const isSelected = selection.includes(block.snapshot.id);
  const justCreated = Date.now() - new Date(block.snapshot.createdAt).getTime() < 2000;
  const autoFocus = editable && justCreated && isSelected;
  const textSubtype = block.observe((state) => state.textType);
  const [editor] = useState(initializeViennaEditor());

  const handleChange = () => {
    const isContentChange = editor.operations.some((op) => op.type !== "set_selection"); // check if the change is a content change
    if (isContentChange) {
      block.debouncedUpdateTextContent(editor.children);
    }
  };

  const handleSlashMenuOptionSelected = (selectedOption: SlashMenuOption) => {
    const selectedType = selectedOption.blockType;
    const subtype = selectedOption.subtype;
    if (selectedType !== BlockType.Text) {
      block.immediatelyUpdateTextContent(editor.children); // the "/" having been deleted, patch an update to the text content. needed only when changing the block type (in other cases, `handleChange` will be called)
      plate.repealAndReplaceTextBlock(block, selectedType);
    } else if (subtype) {
      block.changeTextType(subtype);
    }
  };

  /**
   * Creates an embed if you paste a url
   */
  const handlePaste = (e: React.ClipboardEvent) => {
    const isAlreadyTextInEditor = editor.children.map((n) => Node.string(n)).join("\n");
    if (isAlreadyTextInEditor) return; // don't create an embed if there is already text in the editor

    const pastedText = e.clipboardData.getData("text/plain");
    if (LinkBlock.shouldCreateEmbedFromPastedText(pastedText)) {
      e.preventDefault();
      const textContent = StringUtils.convertToSlateContent(pastedText);
      block.immediatelyUpdateTextContent(textContent); // immediately update text content (and plaintext) so the link block can pick it off as the URL
      plate.repealAndReplaceTextBlock(block, BlockType.Link);
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    // if block is selected (implying also editable), stop prop of right click, enabling spellcheck
    if (isSelected) {
      e.stopPropagation();
    }
  };

  //@critical wtf was this?
  // useEffect(() => {
  //   if (!editable && textContent.length) {
  //   editor.children = textContent;
  //   }
  // }, [textContent]);

  return (
    <StyledTextBlockContent ref={blockRef}>
      <ViennaEditor
        editor={editor}
        initialValue={initialValue}
        handleChange={handleChange}
        readOnly={!editable}
        textSubtype={textSubtype}
        slashMenu={{
          options: SLASH_MENU_OPTIONS,
          onSelect: handleSlashMenuOptionSelected,
          positionRef: blockRef,
        }}
        editorId={block.textEditorId}
        autoFocus={autoFocus}
        onPaste={handlePaste}
        onContextMenu={handleContextMenu}
        wrapperStyles={block.getEditorStyles(theme)}
      />
    </StyledTextBlockContent>
  );
}
